/**
 * Regular Sign-In Card Component
 * Used for Full Time, Part Time, and After School categories
 */
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Clock, Trash2, Edit2, UserPlus, ShieldCheck, User, LogOut } from "lucide-react";
import { format, parseISO, isValid } from "date-fns";
import VoiceInput from "@/components/VoiceInput";
import { useFeatureAccess } from "@/hooks/useFeatureAccess";
import { usePhotoRenewal } from "@/hooks/usePhotoRenewal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Safely format a date string; returns null instead of throwing if missing/invalid
const formatDateSafe = (dateStr, pattern) => {
  if (!dateStr) return null;
  try {
    const parsed = parseISO(dateStr);
    if (!isValid(parsed)) return null;
    return format(parsed, pattern);
  } catch {
    return null;
  }
};

// Photo avatar with error handling + renewal (safe for an unattended, long-open sign-in tablet)
const ChildPhotoAvatar = ({ child, childName, colorClass = "blue" }) => {
  const { src, failed, handleError } = usePhotoRenewal(child?.id, child?.photoUrl);
  const hasValidPhoto = !!src && !failed;
  
  if (hasValidPhoto) {
    return (
      <img
        src={src}
        alt={childName}
        className={`w-16 h-16 rounded-full object-cover border-2 border-${colorClass}-200 flex-shrink-0`}
        onError={handleError}
        onLoad={(e) => { if (e.target.naturalWidth <= 2 || e.target.naturalHeight <= 2) handleError(); }}
      />
    );
  }
  
  return (
    <div className={`w-16 h-16 rounded-full bg-${colorClass}-50 border-2 border-${colorClass}-200 flex items-center justify-center flex-shrink-0`}>
      <User className={`w-7 h-7 text-${colorClass}-300`} />
    </div>
  );
};


export const RegularSignInCard = ({
  signIn,
  child,
  index = 0,
  colorScheme = 'blue',
  onDropOff,
  onClearDropOff,
  onPickUp,
  onClearPickUp,
  onAttendanceToggle,
  onNotesChange,
  onReactivate,
  onManualTimeUpdate,
  onSaveTimeToServer,
  onManualPersonEntry,
  onMidDayOut,
  onMidDayReturn,
  onClearMidDay,
}) => {
  const [editingDropOffTime, setEditingDropOffTime] = useState(false);
  const [editingPickUpTime, setEditingPickUpTime] = useState(false);
  const [showManualDropOff, setShowManualDropOff] = useState(false);
  const [showManualPickUp, setShowManualPickUp] = useState(false);
  const [manualDropOffName, setManualDropOffName] = useState("");
  const [manualDropOffApprover, setManualDropOffApprover] = useState("");
  const [manualPickUpName, setManualPickUpName] = useState("");
  const [manualPickUpApprover, setManualPickUpApprover] = useState("");
  const [showMidDayForm, setShowMidDayForm] = useState(false);
  const [midDayReason, setMidDayReason] = useState('');
  const [midDayCustomReason, setMidDayCustomReason] = useState('');
  const isDeactivated = child && child.active === false;
  
  // Feature access check for voice input
  const { hasAccess: hasVoiceAccess } = useFeatureAccess('voice_input');

  // Format the notes updated info
  const getNotesUpdatedInfo = () => {
    if (signIn.notes && signIn.notes.trim()) {
      // Check if we have staff notes info
      if (signIn.staffNotesUpdatedBy && signIn.staffNotesUpdatedAt) {
        try {
          return `by ${signIn.staffNotesUpdatedBy.split('@')[0]} at ${format(parseISO(signIn.staffNotesUpdatedAt), 'h:mm a')}`;
        } catch {
          return `by ${signIn.staffNotesUpdatedBy.split('@')[0]}`;
        }
      }
      // Check if we have owner notes info
      if (signIn.lastModifiedBy && signIn.lastModifiedAt) {
        try {
          return `by ${signIn.lastModifiedBy.split('@')[0]} at ${format(parseISO(signIn.lastModifiedAt), 'h:mm a')}`;
        } catch {
          return `by ${signIn.lastModifiedBy.split('@')[0]}`;
        }
      }
    }
    return null;
  };

  // Calculate age from birthdate
  const getAge = () => {
    if (!child?.birthdate) return null;
    try {
      const birthDate = parseISO(child.birthdate);
      const today = new Date();
      let years = today.getFullYear() - birthDate.getFullYear();
      let months = today.getMonth() - birthDate.getMonth();
      
      if (months < 0) {
        years--;
        months += 12;
      }
      if (today.getDate() < birthDate.getDate()) {
        months--;
        if (months < 0) {
          years--;
          months += 12;
        }
      }
      
      if (years > 0) {
        return `${years}y ${months}m`;
      }
      return `${months}m`;
    } catch {
      return null;
    }
  };

  const notesInfo = getNotesUpdatedInfo();
  const childAge = getAge();
  
  // Alternating card styles based on color scheme
  const isEvenCard = index % 2 === 0;
  
  // Define gradient backgrounds for each color scheme
  const gradients = {
    blue: 'linear-gradient(135deg, rgba(219,234,254,0.9) 0%, rgba(191,219,254,0.85) 50%, rgba(224,231,255,0.9) 100%)',
    green: 'linear-gradient(135deg, rgba(220,252,231,0.9) 0%, rgba(187,247,208,0.85) 50%, rgba(209,250,229,0.9) 100%)',
    purple: 'linear-gradient(135deg, rgba(243,232,255,0.9) 0%, rgba(233,213,255,0.85) 50%, rgba(245,232,255,0.9) 100%)'
  };
  
  // Define border colors for each scheme
  const borderColors = {
    blue: 'rgba(59,130,246,0.4)',
    green: 'rgba(34,197,94,0.4)',
    purple: 'rgba(168,85,247,0.4)'
  };
  
  const cardBackground = isEvenCard ? gradients[colorScheme] : 'rgba(255,255,255,0.95)';
  const cardBorder = `2px solid ${borderColors[colorScheme]}`;

  return (
    <Card 
      className={`border-0 transition-all duration-200 print:shadow-none print:border print:border-gray-300 ${
        !child ? 'border border-red-300/50' : 
        isDeactivated ? 'opacity-75' : ''
      }`}
      style={{
        background: cardBackground,
        borderRadius: '20px',
        border: !child ? '2px solid rgba(239,68,68,0.5)' : isDeactivated ? '2px solid rgba(156,163,175,0.4)' : cardBorder
      }}
    >
      <CardHeader>
        <CardTitle className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            {/* Child Photo */}
            <ChildPhotoAvatar child={child} childName={signIn.childName} colorClass="blue" />
            <span className={`text-xl ${isDeactivated ? 'text-gray-500' : 'text-blue-900'}`}>
              {signIn.childName}
            </span>
            {childAge && (
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded font-medium">
                {childAge}
              </span>
            )}
            {formatDateSafe(child?.birthdate, 'M/d/yyyy') && (
              <span className="text-xs text-blue-600">
                DOB: {formatDateSafe(child?.birthdate, 'M/d/yyyy')}
              </span>
            )}
            {!child && (
              <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded">
                Child record not found
              </span>
            )}
            {isDeactivated && (
              <div className="flex items-center gap-2">
                <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded">
                  Deactivated {formatDateSafe(child.deactivatedAt, 'MMM d, yyyy') || ''}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-6 text-xs border-blue-500 text-blue-600 hover:bg-blue-50"
                  onClick={() => onReactivate(child)}
                >
                  Reactivate
                </Button>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              checked={!!signIn.dropOffTime}
              onCheckedChange={(checked) => onAttendanceToggle(signIn, checked)}
              data-testid={`attendance-${signIn.id}`}
              className="print:hidden"
            />
            <Label className="text-sm text-blue-700">
              <span className="hidden print:inline font-semibold">
                {signIn.dropOffTime ? "✓ Present" : "✗ Absent"}
              </span>
              <span className="print:hidden">Present</span>
            </Label>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Drop Off */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-blue-900 font-semibold">Drop Off</Label>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 px-2 text-blue-600 hover:bg-blue-50 text-xs"
                onClick={() => setShowManualDropOff(!showManualDropOff)}
                title="Add person not on list"
              >
                <UserPlus className="w-3 h-3 mr-1" />
                {showManualDropOff ? 'Use List' : 'Other'}
              </Button>
            </div>
            
            {showManualDropOff ? (
              /* Manual Drop-Off Entry */
              <div className="space-y-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs text-blue-700 flex items-center gap-1 font-medium">
                  <ShieldCheck className="w-3 h-3" />
                  Person below has written or verbal approval from parent
                </p>
                <Input
                  value={manualDropOffName}
                  onChange={(e) => setManualDropOffName(e.target.value)}
                  placeholder="Enter name of drop-off person"
                  className="border-blue-200 bg-white text-sm"
                />
                <Input
                  value={manualDropOffApprover}
                  onChange={(e) => setManualDropOffApprover(e.target.value)}
                  placeholder="Approved by (parent/guardian name)"
                  className="border-blue-200 bg-white text-sm"
                />
                <Button
                  size="sm"
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white"
                  disabled={!manualDropOffName.trim()}
                  onClick={() => {
                    const approvalNote = manualDropOffApprover.trim() 
                      ? ` [Approved by: ${manualDropOffApprover.trim()}]` 
                      : ' [Manual entry - approval on file]';
                    onManualPersonEntry && onManualPersonEntry(signIn, 'dropOff', manualDropOffName.trim() + approvalNote);
                    setShowManualDropOff(false);
                    setManualDropOffName("");
                    setManualDropOffApprover("");
                  }}
                >
                  Record Drop Off
                </Button>
              </div>
            ) : (
              /* Standard Drop-Off Selection */
              child && child.pickupPersons && child.pickupPersons.length > 0 ? (
                <Select
                  value={signIn.dropOffPerson || ""}
                  onValueChange={(value) => {
                    if (value === "__clear__") {
                      onClearDropOff(signIn);
                    } else {
                      onDropOff(signIn, value);
                    }
                  }}
                >
                  <SelectTrigger className="border-blue-200" data-testid={`dropoff-select-${signIn.id}`}>
                    <SelectValue placeholder="Select drop-off person" />
                  </SelectTrigger>
                  <SelectContent>
                    {signIn.dropOffPerson && (
                      <SelectItem value="__clear__" className="text-red-600">
                        ✕ Clear Selection
                      </SelectItem>
                    )}
                    {child.pickupPersons.map((person, idx) => (
                      <SelectItem key={idx} value={person.name}>
                        {person.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm text-blue-600">No pickup persons configured</p>
              )
            )}
            
            {signIn.dropOffTime && (
              <div className="flex items-center gap-2 text-sm text-blue-700 bg-blue-50 p-2 rounded">
                <Clock className="w-4 h-4" />
                {editingDropOffTime ? (
                  <div className="flex items-center gap-2 flex-1">
                    <Input
                      type="text"
                      value={signIn.dropOffTime || ""}
                      onChange={(e) => onManualTimeUpdate && onManualTimeUpdate(signIn, 'dropOffTime', e.target.value)}
                      onBlur={(e) => {
                        onSaveTimeToServer && onSaveTimeToServer(signIn, 'dropOffTime', e.target.value);
                        setEditingDropOffTime(false);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          onSaveTimeToServer && onSaveTimeToServer(signIn, 'dropOffTime', e.target.value);
                          setEditingDropOffTime(false);
                        }
                      }}
                      placeholder="7:00 AM"
                      className="h-7 border-blue-200 bg-white text-sm w-24"
                      autoFocus
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 px-2 text-blue-600 hover:bg-blue-100"
                      onClick={() => setEditingDropOffTime(false)}
                    >
                      ✓
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 flex-1">
                    <span>Dropped off at <strong>{signIn.dropOffTime}</strong></span>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-5 w-5 p-0 text-blue-600 hover:bg-blue-100"
                      onClick={() => setEditingDropOffTime(true)}
                      title="Edit time"
                      data-testid={`edit-dropoff-${signIn.id}`}
                    >
                      <Edit2 className="w-3 h-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-5 w-5 p-0 text-red-500 hover:bg-red-100"
                      onClick={() => onClearDropOff && onClearDropOff(signIn)}
                      title="Delete drop-off time"
                      data-testid={`delete-dropoff-${signIn.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                )}
              </div>
            )}
            {/* Show approval note if drop-off person has approval info */}
            {signIn.dropOffPerson && signIn.dropOffPerson.includes('[Approved by:') && (
              <p className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded flex items-center gap-1">
                <ShieldCheck className="w-3 h-3" />
                {signIn.dropOffPerson}
              </p>
            )}
          </div>

          {/* Pick Up */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-blue-900 font-semibold">Pick Up</Label>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 px-2 text-blue-600 hover:bg-blue-50 text-xs"
                onClick={() => setShowManualPickUp(!showManualPickUp)}
                title="Add person not on list"
              >
                <UserPlus className="w-3 h-3 mr-1" />
                {showManualPickUp ? 'Use List' : 'Other'}
              </Button>
            </div>
            
            {showManualPickUp ? (
              /* Manual Pick-Up Entry */
              <div className="space-y-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs text-blue-700 flex items-center gap-1 font-medium">
                  <ShieldCheck className="w-3 h-3" />
                  Person below has written or verbal approval from parent
                </p>
                <Input
                  value={manualPickUpName}
                  onChange={(e) => setManualPickUpName(e.target.value)}
                  placeholder="Enter name of pick-up person"
                  className="border-blue-200 bg-white text-sm"
                />
                <Input
                  value={manualPickUpApprover}
                  onChange={(e) => setManualPickUpApprover(e.target.value)}
                  placeholder="Approved by (parent/guardian name)"
                  className="border-blue-200 bg-white text-sm"
                />
                <Button
                  size="sm"
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white"
                  disabled={!manualPickUpName.trim()}
                  onClick={() => {
                    const approvalNote = manualPickUpApprover.trim() 
                      ? ` [Approved by: ${manualPickUpApprover.trim()}]` 
                      : ' [Manual entry - approval on file]';
                    onManualPersonEntry && onManualPersonEntry(signIn, 'pickUp', manualPickUpName.trim() + approvalNote);
                    setShowManualPickUp(false);
                    setManualPickUpName("");
                    setManualPickUpApprover("");
                  }}
                >
                  Record Pick Up
                </Button>
              </div>
            ) : (
              /* Standard Pick-Up Selection */
              child && child.pickupPersons && child.pickupPersons.length > 0 ? (
                <Select
                  value={signIn.pickUpPerson || ""}
                  onValueChange={(value) => {
                    if (value === "__clear__") {
                      onClearPickUp(signIn);
                    } else {
                      onPickUp(signIn, value);
                    }
                  }}
                >
                  <SelectTrigger className="border-blue-200" data-testid={`pickup-select-${signIn.id}`}>
                    <SelectValue placeholder="Select pick-up person" />
                  </SelectTrigger>
                  <SelectContent>
                    {signIn.pickUpPerson && (
                      <SelectItem value="__clear__" className="text-red-600">
                        ✕ Clear Selection
                      </SelectItem>
                    )}
                    {child.pickupPersons.map((person, idx) => (
                      <SelectItem key={idx} value={person.name}>
                        {person.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm text-blue-600">No pickup persons configured</p>
              )
            )}
            
            {signIn.pickUpTime && (
              <div className="flex items-center gap-2 text-sm text-blue-600 bg-blue-50 p-2 rounded">
                <Clock className="w-4 h-4" />
                {editingPickUpTime ? (
                  <div className="flex items-center gap-2 flex-1">
                    <Input
                      type="text"
                      value={signIn.pickUpTime || ""}
                      onChange={(e) => onManualTimeUpdate && onManualTimeUpdate(signIn, 'pickUpTime', e.target.value)}
                      onBlur={(e) => {
                        onSaveTimeToServer && onSaveTimeToServer(signIn, 'pickUpTime', e.target.value);
                        setEditingPickUpTime(false);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          onSaveTimeToServer && onSaveTimeToServer(signIn, 'pickUpTime', e.target.value);
                          setEditingPickUpTime(false);
                        }
                      }}
                      placeholder="5:00 PM"
                      className="h-7 border-blue-200 bg-white text-sm w-24"
                      autoFocus
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 px-2 text-blue-600 hover:bg-blue-100"
                      onClick={() => setEditingPickUpTime(false)}
                    >
                      ✓
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 flex-1">
                    <span>Picked up at <strong>{signIn.pickUpTime}</strong></span>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-5 w-5 p-0 text-blue-600 hover:bg-blue-100"
                      onClick={() => setEditingPickUpTime(true)}
                      title="Edit time"
                      data-testid={`edit-pickup-${signIn.id}`}
                    >
                      <Edit2 className="w-3 h-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-5 w-5 p-0 text-red-500 hover:bg-red-100"
                      onClick={() => onClearPickUp && onClearPickUp(signIn)}
                      title="Delete pick-up time"
                      data-testid={`delete-pickup-${signIn.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                )}
              </div>
            )}
            {/* Show approval note if pick-up person has approval info */}
            {signIn.pickUpPerson && signIn.pickUpPerson.includes('[Approved by:') && (
              <p className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded flex items-center gap-1">
                <ShieldCheck className="w-3 h-3" />
                {signIn.pickUpPerson}
              </p>
            )}
          </div>
        </div>

        {/* Notes */}
        <div className="mt-4">
          <div className="flex items-center justify-between mb-1">
            <Label className="text-blue-900 font-semibold">Notes</Label>
            {signIn.notes && signIn.notes.trim() && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onNotesChange(signIn, "")}
                className="h-6 px-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                title="Clear notes"
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            )}
          </div>
          <div className="relative">
            <Textarea
              value={signIn.notes || ""}
              onChange={(e) => onNotesChange(signIn, e.target.value)}
              placeholder="Add notes..."
              className="border-blue-200 pr-12"
              data-testid={`notes-${signIn.id}`}
            />
            <div className="absolute right-2 top-2 print:hidden">
              {hasVoiceAccess && (
                <VoiceInput
                  onTranscript={(text) => onNotesChange(signIn, (signIn.notes || "") + " " + text)}
                  className="text-blue-600"
                />
              )}
            </div>
          </div>
          {notesInfo && (
            <p className="text-xs text-blue-600 mt-1 text-right">
              {notesInfo}
            </p>
          )}
        </div>

        {/* Mid-Day Departure */}
        {!signIn.midDayOut?.outTime && !showMidDayForm && (
          <button
            className="mt-3 w-full text-left text-xs text-gray-400 border border-dashed border-gray-300 rounded-xl px-3 py-2 hover:border-amber-400 hover:text-amber-600 transition-colors print:hidden"
            onClick={() => setShowMidDayForm(true)}
            data-testid={`mid-day-out-btn-${signIn.id}`}
          >
            + Mid-Day Departure
          </button>
        )}

        {!signIn.midDayOut?.outTime && showMidDayForm && (
          <div className="mt-3 p-3 bg-amber-50 border-2 border-amber-300 rounded-xl space-y-2 print:hidden">
            <p className="text-xs font-semibold text-amber-800 flex items-center gap-1">
              <LogOut className="w-3 h-3" /> Record Mid-Day Departure
            </p>
            <select
              value={midDayReason}
              onChange={e => setMidDayReason(e.target.value)}
              className="w-full border border-amber-200 rounded-lg px-2 py-1.5 text-sm bg-white"
            >
              <option value="">Select reason...</option>
              <option value="Doctor/Dentist Appointment">Doctor/Dentist Appointment</option>
              <option value="Summer Program">Summer Program</option>
              <option value="Field Trip">Field Trip</option>
              <option value="Family Emergency">Family Emergency</option>
              <option value="Other">Other</option>
            </select>
            {midDayReason === 'Other' && (
              <input
                value={midDayCustomReason}
                onChange={e => setMidDayCustomReason(e.target.value)}
                placeholder="Describe reason..."
                className="w-full border border-amber-200 rounded-lg px-2 py-1.5 text-sm bg-white"
              />
            )}
            <div className="flex gap-2">
              <button
                disabled={!midDayReason || (midDayReason === 'Other' && !midDayCustomReason.trim())}
                className="flex-1 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white text-sm rounded-lg py-1.5 font-medium"
                onClick={() => {
                  onMidDayOut && onMidDayOut(signIn, midDayReason, midDayCustomReason);
                  setShowMidDayForm(false);
                  setMidDayReason('');
                  setMidDayCustomReason('');
                }}
              >
                Record Departure
              </button>
              <button
                className="px-3 text-gray-500 hover:text-gray-700 text-sm"
                onClick={() => { setShowMidDayForm(false); setMidDayReason(''); setMidDayCustomReason(''); }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {signIn.midDayOut?.outTime && !signIn.midDayOut?.returnTime && (
          <div className="mt-3 p-3 bg-amber-50 border-2 border-amber-400 rounded-xl print:border print:border-gray-400">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <p className="text-xs font-bold text-amber-800 flex items-center gap-1">
                  <LogOut className="w-3 h-3" /> MID-DAY DEPARTURE
                  <span className="ml-1 bg-amber-500 text-white px-2 py-0.5 rounded-full">OUT SINCE {signIn.midDayOut.outTime}</span>
                </p>
                <p className="text-xs text-amber-700 mt-0.5">
                  {signIn.midDayOut.reason === 'Other' ? signIn.midDayOut.customReason : signIn.midDayOut.reason}
                </p>
              </div>
              <button
                className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-lg font-medium print:hidden"
                onClick={() => onMidDayReturn && onMidDayReturn(signIn)}
                data-testid={`mid-day-return-btn-${signIn.id}`}
              >
                Mark Returned
              </button>
            </div>
          </div>
        )}

        {signIn.midDayOut?.outTime && signIn.midDayOut?.returnTime && (
          <div className="mt-3 p-3 bg-emerald-50 border-2 border-emerald-300 rounded-xl print:border print:border-gray-400">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <p className="text-xs font-bold text-emerald-800 flex items-center gap-1">
                  MID-DAY DEPARTURE
                  <span className="ml-1 bg-emerald-600 text-white px-2 py-0.5 rounded-full">RETURNED {signIn.midDayOut.returnTime}</span>
                </p>
                <p className="text-xs text-emerald-700 mt-0.5">
                  Out: {signIn.midDayOut.outTime} → Back: {signIn.midDayOut.returnTime} &nbsp;|&nbsp;
                  {signIn.midDayOut.reason === 'Other' ? signIn.midDayOut.customReason : signIn.midDayOut.reason}
                </p>
              </div>
              <button
                className="text-xs text-red-500 hover:text-red-700 print:hidden"
                onClick={() => onClearMidDay && onClearMidDay(signIn)}
                data-testid={`mid-day-clear-btn-${signIn.id}`}
              >
                Clear
              </button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RegularSignInCard;
